import React from 'react';
import PageLayout from '../components/layouts/pageLayout';
import {
  Divider,
  Grid2,
  Stack,
  Typography,
  List,
  ListItem,
} from '@mui/material';

// Section Header Template
const SectionHeader = ({ title }) => (
  <Stack spacing={1.25}>
    <Typography variant="h4" fontWeight="600">
      {title}
    </Typography>
    <Divider />
  </Stack>
);

/**
 * Info Section Template
 * @param {string} title - Section title
 * @param {string} description - Optional description displayed before the list
 * @param {Array<Object|string>} items - An array of list items. Each item can be:
 *   - A string, which is displayed as a list item.
 *   - An object with a `title`, `text`, and optional `subItems` array.
 * @param {boolean} [ordered=false] - If `true`, the list will be ordered (numbered). 
 */ 
const InfoSection = ({ title, description, items, ordered = false }) => {
  return (
    <Grid2 container direction="column" spacing={1}>
      <SectionHeader title={title} />
      {description && <Typography variant="body1">{description}</Typography>}

      <List
        sx={{
          listStyleType: ordered ? 'decimal' : 'disc',
          padding: 0,
          pl: 3,
          gap: 1.25,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {items.map((item, index) => (
          <ListItem key={index} sx={{ display: 'list-item', padding: 0 }}>
            {typeof item === 'string' ? (
              <Typography variant="body1">{item}</Typography>
            ) : (
              <>
                <Typography variant="body1" mb={0.5}>
                  <strong>{item.title}</strong> {item.text}
                </Typography>
                {item.subItems && (
                  <List
                    sx={{
                      listStyleType: 'disc',
                      padding: 0,
                      pl: 2,
                      gap: 0.5,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {item.subItems.map((subItem, subIndex) => (
                      <ListItem
                        key={subIndex}
                        sx={{ display: 'list-item', padding: 0 }}
                      >
                        <Typography variant="body1">{subItem}</Typography>
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </ListItem>
        ))}
      </List>
    </Grid2>
  );
};

const InfoPage = () => {
  return (
    <PageLayout>
      {/* Header */}
      <Stack spacing={1.25}>
        <Typography variant="h2">
          Welcome to the New T.M.J Tutoring Dashboard and Logging System!
        </Typography>
        <Typography variant="body1">
          We are thrilled to introduce you to our brand-new Dashboard and
          Logging System! This system has been designed with your needs in mind,
          and we’re confident it will streamline your experience as a T.M.J.
          tutor. Whether you’re logging sessions, viewing your current students,
          or accessing training resources, everything you need is now in one
          convenient place. Let’s dive into the features and functionality that
          make this system exciting and easy to use.
        </Typography>
      </Stack>

      <InfoSection
        title="What and Why: The New System"
        description="Over the years, we’ve heard your feedback about the simplicity and
        flexibility of our previous internal system. Many of you appreciated
        the freedom it provided, and we’ve worked hard to ensure our new
        system maintains this approach. Here’s what you can expect:"
        items={[
          {
            title: 'Simplicity:',
            text: 'The logging system remains straightforward and effective, allowing you to keep track of past and current sessions seamlessly. You can view which sessions have been processed and which are still pending.',
          },
          {
            title: 'Centralised Access:',
            text: 'No more jumping between different platforms! This new system consolidates everything in one place, from logging sessions to accessing training videos and managing your account details.',
          },
        ]}
      />

      <InfoSection
        title="Your Dashboard"
        items={[
          {
            title: 'Quick Links:',
            text: 'Access shortcuts to log sessions, view your students, check your progress within our level-up system, and explore our training video gallery.',
          },
          {
            title: 'Sidebar Navigation:',
            text: 'Expanded pages for logs, student details, and the video gallery can be accessed through the left-hand sidebar.',
          },
          {
            title: 'Account Management:',
            text: 'In the top-right corner, you’ll find settings for your account details, appearance preferences (dark/light mode), and security settings.',
          },
        ]}
      />

      <InfoSection
        title="Logging Sessions"
        description="We’ve worked hard to preserve the simplicity of our log form while introducing some enhancements. Here’s what to keep in mind:"
        ordered
        items={[
          {
            title: 'Accuracy is Key:',
            subItems: [
              'Ensure the method of delivery (in-person, online, or hybrid) is correct for each session.',
              'Double-check that the Session Date matches the actual date the session took place.',
            ],
          },
          {
            title: 'Student Dropdown:',
            text: 'Your student list should accurately reflect your current connections. If any discrepancies arise, such as outdated or incorrect student entries, please notify us.',
          },
          {
            title: 'How It Works:',
            subItems: [
              'Log sessions using the shortcut on the dashboard or through the logs page.',
              'After submitting, your sessions will appear in the full logs page with a “Pending” status. Once payroll processes the session, the status will update to “Processed.”',
              'Use filters to sort sessions by date for easy reference.',
            ],
          },
        ]}
      />

      <InfoSection
        title="Viewing Your Students"
        description="This section provides a clear and concise view of your current students:"
        items={[
          {
            title: 'Key Details:',
            text: 'See important information about each student, such as their name and contact details.',
          },
          {
            title: 'Accuracy:',
            text: 'As mentioned earlier, your student list should be up-to-date. Let us know if there are any discrepancies or outdated entries.',
          },
        ]}
      />

      <InfoSection
        title="Settings, Security, and Customization"
        description="In the top-right corner, you’ll find additional options to customize and secure your account:"
        items={[
          {
            title: 'Account Details:',
            text: 'Update your phone number, email, and name. You can even upload a profile picture!',
          },
          {
            title: 'Security Settings:',
            text: 'Change your password or set up two-factor authentication (2FA) for added security.',
          },
          {
            title: 'Appearance Preferences:',
            text: 'Switch between dark and light mode to suit your preference.',
          },
          {
            title: 'Support:',
            text: 'Access contact points for assistance if any issues arise.',
          },
        ]}
      />

      {/* Footer */}
      <Stack spacing={1}>
        <Typography variant="h2">Conclusion</Typography>
        <Typography variant="body1">
          Thank you for being an integral part of the TMJ Tutoring team! We’re
          incredibly excited to launch this new system and to see how it
          enhances your tutoring experience. Your dedication and engagement mean
          the world to us, and we’re here to support you every step of the way.
        </Typography>
        <Typography
          variant="h4"
          fontWeight="500"
          color="var(--navyBlue-default)"
          textAlign="center"
          p={2}
          pb={0}
        >
          Welcome to 2025 with T.M.J Tutoring—let’s make it our best year yet!
        </Typography>
      </Stack>
    </PageLayout>
  );
};

export default InfoPage;
